import React from "react";
import { TICK } from "Models/constants";
import Divider from "antd/lib/divider";
import Typography from "antd/lib/typography";
import { useSelector } from "react-redux";
import {
    getConsumers,
    getNodes,
    getPace,
    getDescriptionsVisible,
} from "Redux/selectors";
import { Node as INode } from "Models/node";
import Node from "./Node";
import Producer from "./Producer";
import ConsumerGroup from "./ConsumerGroup";
import { VisualizationSection } from "./VisualizationSection";
import { groupToLetter } from "./utils";
import styles from "./Visualization.module.css";
import "./root.css";

const { Paragraph, Title } = Typography;

type VisualizationProps = {
    toggleNode: (node: INode) => void;
};

const Visualization = ({ toggleNode }: VisualizationProps) => {
    const pace = useSelector(getPace);
    const descriptionsVisible = useSelector(getDescriptionsVisible);

    React.useEffect(() => {
        const timeScale = pace * TICK;
        document.documentElement.style.setProperty("--tick", `${timeScale}ms`);
    }, [pace]);

    const nodes = useSelector(getNodes);
    const consumers = useSelector(getConsumers);

    const consumerGroups = React.useMemo(
        () =>
            consumers
                .map((consumer) => consumer.group)
                .filter((group, idx, groups) => groups.indexOf(group) === idx),
        [consumers]
    );

    return (
        <div className={styles.root}>
            <VisualizationSection
                headerTitle="What is Apache Kafka?"
                paragraphs={[
                    "Apache Kafka is a distributed streaming platform for building real-time data pipelines. It allows you to send in-order, persistent messages between applications.",

                    "To process data with Kafka, you need to understand a few unique concepts.",

                    "Kafka consists of servers and clients that communicate via a high-performance TCP network protocol. The communication looks as follows: messages are grouped into topics — a primary Kafka abstraction. The sender (producer) sends messages to a specific topic. The recipient (consumer) receives all messages from a specific topic from many senders. In Kafka, producers and consumers are fully decoupled to achieve the high scalability that Kafka is known for.",
                ]}
                descriptionsVisible={descriptionsVisible}
            />

            <VisualizationSection
                header="Producer:"
                headerTitle="What is a Kafka producer?"
                paragraphs={[
                    "Producers are those client applications that write messages to Kafka. Producers always publish to a category known as a topic. Any message from a given topic sent by any producer that you create will go to every consumer that is listening to that topic.",

                    "A topic logically and physically splits into partitions. A producer partitioner maps each message to a topic partition, and the producer sends a request to the leader of that partition. All writes to the partition must go through the partition leader. In case the leader fails, there may be replicas that will take over its role and acknowledge the write. The number of replicas depends on the configuration.",

                    <>
                        <Title level={5}>TL;DR</Title>
                        <ul>
                            <li>
                                the Kafka producer is conceptually much simpler
                                than the consumer since it has no need for group
                                coordination;
                            </li>
                            <li>
                                producers configuration affects overall
                                throughput, durability and delivery guarantees.
                            </li>
                        </ul>
                    </>,

                    <>
                        <Title level={5}>PRO TIP</Title>
                        Although the exactly-once semantics for message delivery
                        is a Holy Grail in general, Kafka lets you achieve it in
                        some specific cases, for more see{" "}
                        <a
                            href="https://softwaremill.com/what-kafka-exactly-once-really-means/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here.
                        </a>
                    </>,
                ]}
                descriptionsVisible={descriptionsVisible}
            >
                <Producer />
            </VisualizationSection>

            <VisualizationSection
                header="Brokers:"
                headerTitle="What is a Kafka broker?"
                paragraphs={[
                    "A Kafka broker, in other words a Kafka node, is a Kafka server that runs in a Kafka cluster. Kafka Broker receives messages from producers and stores them on disk and later allows consumers to fetch them by topic, partition and offset.",

                    "A Kafka cluster usually consists of 3+ Kafka brokers. Why? Best practice is to have 3 copies of your data. In case of failure, you still have 2 brokers replicating data, so you can potentially survive another failure without data loss.",

                    "If you don’t want to set it up by yourself, there are multiple companies offering Kafka as a Service.",
                ]}
                descriptionsVisible={descriptionsVisible}
            >
                <div>
                    {nodes.map((node, idx) => (
                        <Node node={node} key={idx} toggleNode={toggleNode} />
                    ))}
                </div>
            </VisualizationSection>

            <VisualizationSection
                header="Consumers:"
                headerTitle="What is a Kafka consumer?"
                paragraphs={[
                    "When using Apache Kafka in your system’s architecture, you want to use data produced to Kafka topics in many scenarios. Here’s where the Kafka consumer concept comes to play. Consumers are those client applications that subscribe to one or multiple topics.",

                    "Kafka consumers help your application keep up with the rate of incoming messages, but you cannot rely on a single consumer reading and processing the data. Kafka consumers are typically part of a “consumer group” where each consumer in the group receives messages from a different subset of the partitions in the topic. You can scale the number of consumers, up to the overall number of partitions in a specified topic.",

                    <>
                        <Title level={5}>TL;DR</Title>
                        <ul>
                            <li>
                                a new consumer group can be created for each
                                application that needs all the messages from one
                                or more Kafka topics;
                            </li>
                            <li>
                                new consumers can be added to an existing
                                consumer group to scale data consumptions from a
                                Kafka topic, so each additional consumer in a
                                group will only get a subset of the messages.
                            </li>
                        </ul>
                    </>,

                    <>
                        <Title level={5}>PRO TIP</Title>
                        When creating a topic, you have to think about the
                        future. The number of partitions affects the maximum
                        number of consumers actively processing messages in a
                        single consumer group.
                    </>,
                ]}
                descriptionsVisible={descriptionsVisible}
            >
                <div>
                    {consumerGroups.map((group) => (
                        <ConsumerGroup
                            key={group}
                            consumers={consumers.filter(
                                (consumer) => consumer.group === group
                            )}
                            name={groupToLetter(parseInt(group))}
                        />
                    ))}
                </div>
            </VisualizationSection>

            {descriptionsVisible && (
                <section className={styles.bottom}>
                    <Divider />

                    <Paragraph>
                        Want to dive deeper into Apache Kafka? Go to{" "}
                        <a
                            href="https://softwaremill.com/blog/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            SoftwareMill Tech Blog.
                        </a>
                    </Paragraph>
                    <Paragraph>
                        Reap the benefits of real-time data streaming and Apache
                        Kafka with a trusted and experienced{" "}
                        <a
                            href="https://softwaremill.com/contact/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Confluent Partner.
                        </a>
                    </Paragraph>
                </section>
            )}
        </div>
    );
};

export default Visualization;
